/**
 * Hot Folder Service
 *
 * API calls for hot folder management
 */

import { get, post } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'
import type {
  HotFolder,
  PublicHotFolder,
  CreateHotFolderData,
  UpdateHotFolderData,
  HotFolderFile,
  HotFolderFileSearchFilters,
  UpdateOverrideData,
  ConnectionTestResult,
  ScanResult,
  HotFolderStats,
  ProcessFileResult,
  BatchProcessResult,
  SchedulerStatus,
} from '@/domain/hot-folder/types'

// =============================================================================
// Hot Folder CRUD
// =============================================================================

/**
 * Get list of hot folders
 */
export async function getHotFolders(): Promise<PublicHotFolder[]> {
  return get<PublicHotFolder[]>(API_ROUTES.HOT_FOLDERS.BASE)
}

/**
 * Get hot folder by ID
 */
export async function getHotFolderById(id: string): Promise<HotFolder> {
  return get<HotFolder>(API_ROUTES.HOT_FOLDERS.BY_ID(id))
}

/**
 * Create a new hot folder
 */
export async function createHotFolder(data: CreateHotFolderData): Promise<HotFolder> {
  return post<HotFolder>(API_ROUTES.HOT_FOLDERS.BASE, data)
}

/**
 * Update a hot folder
 */
export async function updateHotFolder(
  id: string,
  data: UpdateHotFolderData
): Promise<HotFolder> {
  return post<HotFolder>(API_ROUTES.HOT_FOLDERS.UPDATE(id), data)
}

/**
 * Delete a hot folder
 */
export async function deleteHotFolder(id: string): Promise<void> {
  return post(API_ROUTES.HOT_FOLDERS.DELETE(id), {})
}

/**
 * Toggle hot folder enabled/disabled
 */
export async function toggleHotFolder(id: string): Promise<HotFolder> {
  return post<HotFolder>(API_ROUTES.HOT_FOLDERS.TOGGLE(id), {})
}

/**
 * Test connection to a folder path
 */
export async function testConnection(
  folderPath: string,
  folderType: 'local' | 'smb',
  credentials?: {
    networkUsername?: string
    networkPassword?: string
    networkDomain?: string
  }
): Promise<ConnectionTestResult> {
  return post<ConnectionTestResult>(API_ROUTES.HOT_FOLDERS.TEST_CONNECTION, {
    folderPath,
    folderType,
    ...credentials,
  })
}

// =============================================================================
// Hot Folder Files
// =============================================================================

/**
 * Get files for a hot folder
 */
export async function getHotFolderFiles(
  hotFolderId: string,
  filters?: Omit<HotFolderFileSearchFilters, 'hotFolderId'>
): Promise<HotFolderFile[]> {
  const params = new URLSearchParams()

  if (filters?.status) params.append('status', filters.status)
  if (filters?.modality) params.append('modality', filters.modality)
  if (filters?.detectedFrom) params.append('detectedFrom', filters.detectedFrom)
  if (filters?.detectedTo) params.append('detectedTo', filters.detectedTo)
  if (filters?.limit) params.append('limit', String(filters.limit))
  if (filters?.offset) params.append('offset', String(filters.offset))

  const queryString = params.toString()
  const url = queryString
    ? `${API_ROUTES.HOT_FOLDERS.FILES(hotFolderId)}?${queryString}`
    : API_ROUTES.HOT_FOLDERS.FILES(hotFolderId)

  const response = await get<{ files: HotFolderFile[]; total: number }>(url)
  return response.files
}

/**
 * Get hot folder statistics
 */
export async function getHotFolderStats(hotFolderId: string): Promise<HotFolderStats> {
  return get<HotFolderStats>(API_ROUTES.HOT_FOLDERS.STATS(hotFolderId))
}

/**
 * Trigger manual scan
 */
export async function scanHotFolder(hotFolderId: string): Promise<ScanResult> {
  return post<ScanResult>(API_ROUTES.HOT_FOLDERS.SCAN(hotFolderId), {})
}

interface ResetResult {
  message: string
  resetFilesCount: number
  deletedFilesCount: number
}

/**
 * Reset hot folder files
 * - Without deleteAll: resets COMPLETED/FAILED files back to DETECTED for reprocessing
 * - With deleteAll=true: deletes all file records (requires re-scan)
 */
export async function resetHotFolder(
  hotFolderId: string,
  deleteAll: boolean = false
): Promise<ResetResult> {
  const url = deleteAll
    ? `${API_ROUTES.HOT_FOLDERS.RESET(hotFolderId)}?deleteAll=true`
    : API_ROUTES.HOT_FOLDERS.RESET(hotFolderId)
  return post<ResetResult>(url, {})
}

/**
 * Get scheduler status
 */
export async function getSchedulerStatus(): Promise<SchedulerStatus> {
  return get<SchedulerStatus>(API_ROUTES.HOT_FOLDERS.SCHEDULER_STATUS)
}

// =============================================================================
// File Processing
// =============================================================================

/**
 * Process a single file
 */
export async function processFile(fileId: string): Promise<ProcessFileResult> {
  return post<ProcessFileResult>(API_ROUTES.HOT_FOLDER_FILES.PROCESS(fileId), {})
}

/**
 * Attachment input for process with attachments
 */
export type ProcessAttachmentInput = {
  attachmentType: string
  fileName: string
  fileData: string // Base64 encoded
  mimeType?: string
  description?: string
}

/**
 * Result from processing with attachments
 */
export type ProcessWithAttachmentsResult = ProcessFileResult & {
  attachmentsCreated: number
  thumbnailsGenerated: number
}

/**
 * Process a single file with required attachments
 * Requires at least one CLINICAL_HISTORY attachment
 * Uses extended timeout (2 min) for file upload operations
 */
export async function processFileWithAttachments(
  fileId: string,
  attachments: ProcessAttachmentInput[],
  priorityId?: string
): Promise<ProcessWithAttachmentsResult> {
  return post<ProcessWithAttachmentsResult>(
    API_ROUTES.HOT_FOLDER_FILES.PROCESS_WITH_ATTACHMENTS(fileId),
    { attachments, priorityId },
    { timeout: 120000 } // 2 minutes for single file with attachments
  )
}

/**
 * Process multiple files
 */
export async function processBatch(fileIds: string[]): Promise<BatchProcessResult> {
  return post<BatchProcessResult>(API_ROUTES.HOT_FOLDER_FILES.PROCESS_BATCH, { fileIds })
}

/**
 * Result from batch processing with attachments
 */
export type BatchWithAttachmentsResult = {
  success: number
  errors: number
  studyId: string | null
  patientId: string | null
  attachmentsCreated: number
  thumbnailsGenerated: number
  details: {
    processed: Array<{
      fileId: string
      studyId: string | null
      isNewStudy: boolean
    }>
    failed: Array<{
      type: string
      message: string
    }>
  }
}

/**
 * Process multiple files with required attachments
 * Requires at least one CLINICAL_HISTORY attachment
 * Uses extended timeout (5 min) for large batch operations
 */
export async function processBatchWithAttachments(
  fileIds: string[],
  attachments: ProcessAttachmentInput[],
  priorityId?: string
): Promise<BatchWithAttachmentsResult> {
  return post<BatchWithAttachmentsResult>(
    API_ROUTES.HOT_FOLDER_FILES.PROCESS_BATCH_WITH_ATTACHMENTS,
    { fileIds, attachments, priorityId },
    { timeout: 300000 } // 5 minutes for batch processing with uploads
  )
}

/**
 * Skip a file
 */
export async function skipFile(fileId: string): Promise<HotFolderFile> {
  return post<HotFolderFile>(API_ROUTES.HOT_FOLDER_FILES.SKIP(fileId), {})
}

/**
 * Update override fields for a file
 */
export async function updateOverride(
  fileId: string,
  data: UpdateOverrideData
): Promise<HotFolderFile> {
  return post<HotFolderFile>(API_ROUTES.HOT_FOLDER_FILES.OVERRIDE(fileId), data)
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Format DICOM patient name for display
 * DICOM format: "LASTNAME^FIRSTNAME^MIDDLENAME"
 */
export function formatDicomPatientName(dicomName: string | null): string {
  if (!dicomName) return 'Sin nombre'

  const parts = dicomName.split('^')
  const lastName = parts[0] || ''
  const firstName = parts[1] || ''
  const middleName = parts[2] || ''

  const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ')
  return fullName || dicomName
}

/**
 * Format DICOM date for display
 * DICOM format: "YYYYMMDD" or ISO "YYYY-MM-DD"
 */
export function formatDicomDate(dicomDate: string | null): string {
  if (!dicomDate) return ''

  const trimmed = dicomDate.trim()
  if (!trimmed) return ''

  try {
    // Handle already formatted dates (ISO format)
    if (trimmed.includes('-')) {
      const date = new Date(trimmed)
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('es-CO', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      }
    }

    // Parse DICOM format (YYYYMMDD)
    if (trimmed.length >= 8 && /^\d{8}/.test(trimmed)) {
      const year = trimmed.substring(0, 4)
      const month = trimmed.substring(4, 6)
      const day = trimmed.substring(6, 8)
      const date = new Date(`${year}-${month}-${day}T00:00:00`)
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('es-CO', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      }
    }
  } catch {
    // Ignore parse errors
  }

  return trimmed
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`
}

/**
 * Get display name for a file (patient name or filename)
 */
export function getFileDisplayName(file: HotFolderFile): string {
  if (file.overridePatientFirstName && file.overridePatientLastName) {
    return `${file.overridePatientFirstName} ${file.overridePatientLastName}`
  }
  if (file.dicomPatientName) {
    return formatDicomPatientName(file.dicomPatientName)
  }
  return file.fileName
}
